import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MapPin, Clock, Star, Edit, Loader2, CheckCircle, Calendar } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { ServiceCard } from '../components/common/ServiceCard';
import { RatingStars } from '../components/common/RatingStars';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import ImageUpload from '../components/common/ImageUpload';
import { GamificationBadges, StreakCounter, ReferralCode } from '../components/common/Gamification';
// import ImageUpload from '../components/common/ImageUpload';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, api, updateUser, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: '',
    bio: '',
    location: '',
    phone: '',
    avatar_url: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
    setEditForm({
      full_name: user?.full_name || '',
      bio: user?.bio || '',
      location: user?.location || '',
      phone: user?.phone || '',
      avatar_url: user?.avatar_url || '',
    });
  }, [isAuthenticated, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [servicesRes, reviewsRes] = await Promise.all([
        api.get('/my-services'),
        api.get(`/reviews/user/${user?.id}`),
      ]);
      setServices(servicesRes.data.filter((s) => s.active));
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await updateUser(editForm);
    setSaving(false);

    if (result.success) {
      toast.success('Profil mis à jour');
      setEditModalOpen(false);
      refreshUser();
    } else {
      toast.error(result.error);
    }
  };

  if (!isAuthenticated || !user) return null;

  return (
    <Layout>
      <div className="container-app py-8 md:py-12">
        {/* Profile Header */}
        <Card className="rounded-2xl overflow-hidden mb-8">
          <div className="h-32 bg-gradient-to-r from-primary/20 to-emerald-500/20" />
          <CardContent className="relative p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <Avatar className="w-24 h-24 -mt-16 ring-4 ring-background">
                <AvatarImage src={user.avatar_url} />
                <AvatarFallback className="bg-primary text-primary-foreground text-3xl">
                  {user.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-2xl md:text-3xl font-bold">{user.full_name}</h1>
                  {user.verified && (
                    <CheckCircle className="w-6 h-6 text-primary" />
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-4">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {user.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Membre depuis {format(new Date(user.created_at), 'MMMM yyyy', { locale: fr })}
                  </span>
                </div>

                {user.bio && (
                  <p className="text-muted-foreground mb-4">{user.bio}</p>
                )}

                <div className="flex flex-wrap gap-4">
                  <div className="credit-badge text-base">
                    <Clock className="w-4 h-4 text-secondary" />
                    <span className="font-bold">{user.credits_balance}</span>
                    <span>crédits</span>
                  </div>
                  <Badge variant="secondary" className="text-base py-1">
                    {user.total_exchanges} échanges
                  </Badge>
                  {user.rating_count > 0 && (
                    <div className="flex items-center gap-2">
                      <RatingStars rating={user.rating_average} count={user.rating_count} />
                    </div>
                  )}
                </div>
              </div>

              <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full" data-testid="edit-profile-btn">
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-profile-dialog">
                  <DialogHeader>
                    <DialogTitle>Modifier mon profil</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveProfile} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Nom complet</Label>
                      <Input
                        id="edit-name"
                        value={editForm.full_name}
                        onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-bio">Bio</Label>
                      <Textarea
                        id="edit-bio"
                        value={editForm.bio}
                        onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                        placeholder="Parlez de vous..."
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-location">Localisation</Label>
                      <Input
                        id="edit-location"
                        value={editForm.location}
                        onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                        className="rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-phone">Téléphone</Label>
                      <Input
                        id="edit-phone"
                        type="tel"
                        value={editForm.phone}
                        onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                        placeholder="+32 4XX XX XX XX"
                        className="rounded-xl"
                        data-testid="edit-phone-input"
                      />
                    </div>
                    <ImageUpload
                      label="Photo de profil"
                      currentImage={editForm.avatar_url}
                      onUpload={(url) => setEditForm({ ...editForm, avatar_url: url })}
                      aspectRatio="square"
                    />
                    <Button type="submit" className="w-full btn-primary sticky bottom-0" disabled={saving} data-testid="save-profile-btn">
                      {saving ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Enregistrer
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Services */}
        <Card className="rounded-2xl mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Mes services ({services.length})</CardTitle>
              <Link to="/services/new">
                <Button variant="outline" className="rounded-full" size="sm">
                  Ajouter
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : services.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => (
                  <ServiceCard key={service.id} service={service} />
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Aucun service actif
              </p>
            )}
          </CardContent>
        </Card>

        {/* Reviews */}
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>Avis reçus ({reviews.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {reviews.length > 0 ? (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-border pb-4 last:border-0 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={review.reviewer?.avatar_url} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {review.reviewer?.full_name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{review.reviewer?.full_name}</span>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), 'dd MMM yyyy', { locale: fr })}
                          </span>
                        </div>
                        <RatingStars rating={review.rating} showCount={false} size="sm" />
                        {review.comment && (
                          <p className="text-muted-foreground mt-2">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                Aucun avis reçu pour le moment
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
